/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @next/next/no-img-element */

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  AppBar, Box, Button, Collapse, Divider, Drawer, IconButton, InputBase,
  List, ListItemButton, ListItemText, Menu, MenuItem, Paper, Stack, Tooltip, Typography,
  Dialog, DialogContent, DialogContentText, DialogTitle, CircularProgress, Avatar, ListItemIcon,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import Settings from "@mui/icons-material/Settings";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import Logout from "@mui/icons-material/Logout";
import { deepOrange } from "@mui/material/colors";
import { useTheme } from "@mui/material/styles";
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled';
import CarCrashIcon from '@mui/icons-material/CarCrash';
import { useRouter } from "next/router";
import { useSession, signIn, signOut } from "next-auth/react";
import { useDispatch, useSelector } from "react-redux";
import {
  socialSignup,
  getProfile,
  getUserData,
  clearUserDetail,
} from "../../../redux/slices/user";
import { motion, AnimatePresence } from "framer-motion";
import HEADER_RSLHOME from "../../../data/header-rslhome";
import CtaButton from "../../buttonWithArrow";
import SignIn from "../../sign-in/sign-in";
import SignUp from "../../sign-up/sign-up";
import { usePathname } from "next/navigation";
import { getChaufferPlanInfo } from "../../../redux/slices/investments";
import { getFutureBookingList } from '../../../redux/slices/common'
import DriverStatusCard from "../../GuestBooking/tripUpdate";
import shouldShowPlayStore from "../../../utils/shouldShowPlayStore";

const routeMap = {
  CAB: ['/carBooking/carBookRide'],
  FLIGHTS: ['/flights', '/flights/flights', '/flights/passengerInfo'],
  HOTELS: [
    '/hotels',
    '/hotels/hotels',
    '/hotels/hotel-details/[id]',
    '/hotels/booking-page'
  ],
  ACTIVITIES: [
    '/activities',
    '/activities/activities',
    '/activities/activity-details',
    '/activities/booking-page'
  ],
  HOLIDAYS: [
    '/holidays',
    '/holidays/holidays',
    '/holidaypackage/holiday-details',
    '/holidaypackage/booking-page'
  ],
  STAYCATION: [
    '/staycations',
    '/staycation/staycations',
    '/staycation/staycation-details',
    '/staycation/booking-page'
  ],
  BUSES: [
    '/buses',
    '/busBooking/busRentals',
    '/busBooking/busBookRide',
    '/busrental/bus-details',
    '/busrental/booking-page'
  ],
  "VISA": [
    '/visa-service/visaService',
    '/visa-service/visadetail',
    '/visa-service'
  ]
}

function useIsActive(href) {
  const pathname = usePathname();                   // e.g. "/info/supplierPartnerb2b"
  const current = normalizePath(pathname);
  const target = normalizePath(href);              // works for "/x?y=1" too
  return current === target;
}

const isEqualPath = (currentPath, href) =>
  normalizePath(currentPath) === normalizePath(href);

export default function TopNav({ config = HEADER_RSLHOME, bgColor, borderColor }) {
  const theme = useTheme();
  const router = useRouter();
  const dispatch = useDispatch();
  const pathname = usePathname(); // safe: called once, top-level
  const isActivePath = useCallback(
    (href) => isEqualPath(pathname, href),
    [pathname]
  );

  const { futureBookingList } = useSelector(state => state.common)
  // ── design/behavior (same) ────────────────────────────────────────────────
  const [baseOffset, setBaseOffset] = useState(32);
  const [offset, setOffset] = useState(32);
  const [isSignSession, setIsSignSession] = useState(
    sessionStorage.getItem('isSig')
  );
  const [openReg, setOpenReg] = useState(false);
  const offsetRef = useRef(offset);
  offsetRef.current = offset;

  const [menuAnchors, setMenuAnchors] = useState({});
  const openMenu = (i, el) => setMenuAnchors((p) => ({ ...p, [i]: el }));
  const closeMenu = (i) => setMenuAnchors((p) => ({ ...p, [i]: null }));
  const [isDark, setIsDark] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const toggleExpand = (i) => setExpanded((p) => ({ ...p, [i]: !p[i] }));
  const [isBooking, setIsBooking] = useState(false)
  const [isAbout, setIsAbout] = useState(false)
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    const currentPath = window.location.pathname

    // Paths that should set isAbout = true
    const aboutPaths = new Set([
      '/about-rsl',
      '/investments/investments',
      '/info/investorForm',
      '/jobDetails/_id',
      '/visa-service/visaService',
      '/info/vendor',
      '/info/vendorForm',
      '/info/supplier',
      '/info/supplierPartner',
      '/info/travelForm',
      '/sign-in/sign-in',
      '/visa-service/uae-tourist',
      '/attraction/details',
      '/blog/blogs',
      '/blog/blog-details/[id]',
      '/privacyUrl',
      '/privacypolicy',
      '/contactUs',
      '/locateUs',
      '/legacyandpolicy',
      '/legacyandpolicymobile',
      '/careers',
      '/jobDetails',
      '/termsAndPolicies',
      '/rsldashboard/db-settings',
      '/rsldashboard/db-cardDetails',
      '/guestbooking/db-settings',
      '/web-booker',
      '/services',
      '/info/supplierPartnerb2b',
    ])

    // Paths that should set both isAbout = true and isBooking = true
    const bookingPaths = new Set([
      '/flights/flights',
      '/carBooking/carRentals',
      '/carBooking/carBookRide',
      '/busBooking/busRentals',
      '/busBooking/busBookRide',
      '/busrental/booking-page',
      '/carrentalnew/car-details',
      '/buses/busBooking',
      '/busrental/bus-details',
      '/safari/safari',
      '/safari/safari-details',
      '/safari/booking-page',
      '/hotels/hotels',
      '/hotels/booking-page',
      '/hotels/hotel-details',
      '/activities/activity-details',
      '/activities/activities',
      '/activities',
      '/holidays',
      '/holidays/holidays',
      '/staycations',
      '/buses',
      '/flights',
      '/hotels',
      '/activities/booking-page',
      '/holidays/holidays-details',
      '/holidays/booking-page',
      '/tourpackage/tour-details',
      '/tourpackage/booking-page',
      '/carrentalnew/booking-page',
      '/holidaypackage/booking-page',
      '/holidaypackage/holiday-details',
      '/staycation/staycations',
      '/staycation/staycation-details',
      '/staycation/booking-page',
      '/flights/passengerInfo',
      '/rsldashboard/bookinglist',
      '/guestbooking/bookinglist',
      '/visa-service/visaService',
      '/visa-service/visadetail',
      '/visa-service'
    ])


    // Paths that match dynamically (Includes Substrings)
    const dynamicPaths = [
      '/location/location-details/',
      '/location/sub-location/',
      '/blog/blog-details/',
      '/hotels/hotel-details'
      // '/visa-service/visadetail',
      // '/visa-service/'
    ]

    //for ATM dark
    const darkPath = new Set([
      '/info/investor',
      '/services',
      '/info/supplierPartnerb2b',
      '/web-booker',
      '/company-profile',
      '/supplier',
      '/franchise-faq'
    ])

    // Check and set values dynamically
    if (aboutPaths.has(currentPath)) {
      setIsAbout(true)
    }

    // if (darkPath.has(currentPath)) {
    //   setIsDark(true)
    //   setIsAbout(false)
    // }

    // if ([...bookingPaths].some(path => currentPath.startsWith(path))) {
    //   setIsAbout(false)
    //   setIsBooking(true)
    // }


    if (dynamicPaths.some(path => currentPath.includes(path))) {
      setIsAbout(true)
    }
  }, [])


  useEffect(() => {
    const mql = window.matchMedia("(min-width: 900px)");
    const computeBase = () => setBaseOffset(mql.matches ? 32 : 16);
    computeBase();
    mql.addEventListener?.("change", computeBase);
    return () => mql.removeEventListener?.("change", computeBase);
  }, []);

  useEffect(() => {
    setIsSignSession(sessionStorage.getItem('isSig'))
  }, [sessionStorage.getItem('isSig')])

  useEffect(() => {
    if (isSignSession !== null && isSignSession !== '') {
      setOpenReg(true)
    }
  }, [isSignSession])


  const goSearch = useCallback((e) => {
    e.preventDefault();           // don't submit the form / don't focus
    router.push("/guestbooking/bookinglist");       // <-- your target route
  }, [router]);

  const goSearchKey = useCallback((e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      router.push("/guestbooking/bookinglist");
    }
  }, [router]);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY || 0;
        const next = Math.max(0, Math.min(baseOffset, baseOffset - y));
        if (next !== offsetRef.current) setOffset(next);
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [baseOffset]);

  const pinned = offset <= 1;

  // ── auth flow state (from old header) ─────────────────────────────────────
  const { data: session } = useSession();
  const { userDetail, socialSignupResponse } = useSelector((s) => s.user);
  const { chaufferPlanInfoData } = useSelector((s) => s.investment);

  const [isSignUp, setIsSignUp] = useState(false);
  const [viewStatus, setViewStatus] = useState(true);
  const [printMsg, setPrintMsg] = useState(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refTocken, setRefTocken] = useState("");
  const [showCard, setShowCard] = useState(false);
  const [showStatusCard, setShowStatusCard] = useState(false);
  const [tripId, setTripId] = useState(null);
  const [accountEl, setAccountEl] = useState(null);
  const accountOpen = Boolean(accountEl);
  const handleAccountOpen = (e) => setAccountEl(e.currentTarget);
  const handleAccountClose = () => setAccountEl(null);
  const [chauffeurMessage, setChauffeurMessage] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isSignInUpCompleted, setIsSignInUpCompleted] = useState(
    () => (typeof window !== "undefined" ? localStorage.getItem("isSignInUpCompleted") : "false")
  );

  // helper: email to show on avatar
  const getStored = (k) => {
    if (typeof window === "undefined") return "";
    const v = sessionStorage.getItem(k);
    try { return v ? JSON.parse(v) : ""; } catch { return v || ""; }
  };
  const displayEmail =
    session?.customuser?.responseData?.email ||
    session?.customuser?.email ||
    session?.user?.email ||
    getStored("userEmail");

  const initials = displayEmail ? displayEmail.slice(0, 1).toUpperCase() : "U";
  const isLoggedIn = isSignInUpCompleted === "true" && !!displayEmail;

  const getCookie = (name) => {
    const cookieValue = typeof document !== "undefined" ? document.cookie.split("; ").find((row) => row.startsWith(`${name}=`)) : null;
    return cookieValue ? cookieValue.split("=")[1] : null;
  };

  useEffect(() => setRefTocken(getCookie("apiToken") || ""), [typeof document !== "undefined" ? document.cookie : ""]);

  useEffect(() => {
    const flag = sessionStorage.getItem("isSig");
    if (flag) setOpenReg(true);
  }, []);

  async function handleGoogleSignin() {
    try {
      setLoading(true);
      await signIn("google", { redirect: false });
    } finally {
      setLoading(false);
    }
  }

  const handleSessionClear = () => {
    sessionStorage.removeItem("rslkey");
    sessionStorage.removeItem("rslkeyId");
    sessionStorage.removeItem("userEmail");
    sessionStorage.removeItem("userName");
    sessionStorage.removeItem("countryCode");
    sessionStorage.removeItem("phone");
    sessionStorage.removeItem("isSig");
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("refreshToken");
  };

  useEffect(() => {
    const passengerId = JSON.parse(sessionStorage.getItem("rslkeyId") || "null");
    if (passengerId) {
      const obj = { passengerId };
      dispatch(getChaufferPlanInfo(obj));
    }
  }, [dispatch]);

  useEffect(() => {
    if (chaufferPlanInfoData) {
      if (chaufferPlanInfoData?.status === 1 && chaufferPlanInfoData?.detail?.hasPersonalChauffeur) {
        setChauffeurMessage(`${chaufferPlanInfoData?.detail?.expiryMessage ? chaufferPlanInfoData?.detail?.expiryMessage : chaufferPlanInfoData?.message} - "${chaufferPlanInfoData?.detail?.planName}"`)
        setIsExpired(chaufferPlanInfoData?.detail?.expiryMessage ? true : false);
      } else {
        setChauffeurMessage(null);
        setIsExpired(false);
      }
    }
  }, [chaufferPlanInfoData])

  const handleLogout = async () => {
    handleAccountClose();
    handleSessionClear();
    dispatch(clearUserDetail());
    localStorage.setItem("isSignInUpCompleted", "false");
    await signOut({ redirect: router.asPath.includes("rsldashboard"), callbackUrl: "/" });
    window.location.reload();
  };

  const handleCloseReg = () => {
    setOpenReg(false);
    setOtpLoading(false);
    setPrintMsg(null);
    sessionStorage.setItem("isSig", "");
    setIsSignUp(false);
    setViewStatus(true);
  };

  // session reactions (OTP/Google) — same logic simplified
  useEffect(() => {
    if (!session) return;

    if (session?.customuser && Object.keys(session.user || {}).length === 0) {
      const s = session.customuser;

      if (s?.handledStatus === -12 && !viewStatus) {
        setPrintMsg("please enter valid OTP");
        setOtpLoading(false);
        return;
      }

      if (s?.responseData?.userId) {
        if (s.handledStatus === 12) {
          setIsSignUp(true);
          setOtpLoading(false);
          setPrintMsg("");
        } else if (s.handledStatus === 11 || s.handledStatus === 21) {
          sessionStorage.removeItem("isSig");
          localStorage.setItem("isSignInUpCompleted", "true");
          setPrintMsg(null);
          setOpenReg(false);
          setOtpLoading(false);
          setViewStatus(true);
          dispatch(getProfile({ rslId: s?.responseData?.rslId }));
        } else if (s.handledStatus === 23) {
          setOtpLoading(false);
          setPrintMsg(s?.message);
          setIsSignUp(true);
        }
      }
      return;
    }

    if (session?.googleId && Object.keys(session?.user || {}).length > 0) {
      sessionStorage.removeItem("isSig");
      localStorage.setItem("isSignInUpCompleted", "true");
      setPrintMsg(null);
      setOpenReg(false);
      setOtpLoading(false);
      setViewStatus(true);

      dispatch(
        socialSignup({
          device_id: "",
          device_token: "",
          device_type: "1",
          email: session?.customuser?.email,
          facebook_id: session?.customuser?.id,
          first_name: session?.customuser?.name,
          image_url: session?.customuser?.image,
          lang: "en",
          last_name: "",
          latitude: 12.9,
          longitude: 365.9,
          request_type: 2,
        })
      );
    }
  }, [session]);

  useEffect(() => {
    if (!socialSignupResponse?.responseData) return;
    const r = socialSignupResponse.responseData;
    const cc = r?.countryCode ? JSON.stringify(r.countryCode) : "";
    dispatch(
      getUserData({
        id: r?._id,
        rslkeyId: r?.rslId,
        phone: r?.mobile,
        countryCode: cc,
        name: r?.name,
        email: r?.email,
        specialRequest: "",
      })
    );
    sessionStorage.setItem("rslkey", JSON.stringify(r?._id));
    sessionStorage.setItem("rslkeyId", JSON.stringify(r?.rslId));
    sessionStorage.setItem("phone", JSON.stringify(r?.mobile));
    sessionStorage.setItem("countryCode", cc);
    sessionStorage.setItem("userName", JSON.stringify(r?.name));
    sessionStorage.setItem("userEmail", JSON.stringify(r?.email));
  }, [socialSignupResponse]);

  useEffect(() => {
    if (!userDetail?.responseData) return;
    const d = userDetail.responseData;
    dispatch(
      getUserData({
        id: d?._id,
        rslkeyId: d?.rslId,
        phone: d?.mobile,
        countryCode: d?.countryCode,
        name: d?.name,
        email: d?.email,
        specialRequest: "",
      })
    );
    sessionStorage.setItem("rslkey", JSON.stringify(d?._id));
    sessionStorage.setItem("rslkeyId", JSON.stringify(d?.rslId));
    sessionStorage.setItem("phone", JSON.stringify(d?.mobile));
    sessionStorage.setItem("countryCode", JSON.stringify(d?.countryCode));
    sessionStorage.setItem("userName", JSON.stringify(d?.name));
    sessionStorage.setItem("userEmail", JSON.stringify(d?.email));
  }, [userDetail]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const updated = localStorage.getItem("isSignInUpCompleted");
    if (updated) setIsSignInUpCompleted(updated);
  }, [typeof window !== "undefined" ? localStorage.getItem("isSignInUpCompleted") : ""]);

  useEffect(() => {
    const passengerId = JSON.parse(sessionStorage.getItem("rslkeyId"));

    {passengerId && dispatch(getFutureBookingList({ passengerId }))}
    // const obj = {
    //   "passenger_id": passengerId
    // }
    // dispatch(getFutureBookingList(obj));
  }, [dispatch])

  useEffect(() => {
    if (futureBookingList && futureBookingList?.details?.trip_id) {
      setShowCard(true);
      setTripId(futureBookingList?.details?.trip_id);
    }

  }, [futureBookingList])

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        top: `${offset}px`,
        transition: "top .12s ease-out",
        background: "transparent",
        boxShadow: "none",
        // px: { xs: 1.5, md: 3 },
        px: { xs: 1.5, md: 2 },
        zIndex: 1100,
      }}
    >
      <Paper
        elevation={pinned ? 6 : 0}
        className="mx-auto"
        sx={{
          // maxWidth: 1352,
          maxWidth: 1400,
          width: "100%",
          height: { xs: 55, sm: 65, md: 70, lg: 84 },
          px: { xs: 2, md: 2 },
          py: 0,
          borderRadius: "20px",
          backgroundColor: bgColor ? bgColor : "none",
          borderColor: borderColor ? borderColor : "transparent",
          // border: "1px solid",
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, md: 0 },
          backdropFilter: "saturate(180%) blur(8px)",
          boxShadow: "none",
        }}
      >
        {/* Logo */}
        <Link href={config.logo.href || "/"} className="d-flex align-items-center text-decoration-none">
          <Box sx={{ width: { xs: 75, lg: 96 }, height: { xs: 45, lg: 56 }, position: "relative" }}>
            <Image src={config.logo.src} alt="RSL" fill style={{ objectFit: "contain" }} />
          </Box>
        </Link>

        {/* Desktop center nav */}
        <Stack direction="row" alignItems="center" spacing={2} className="d-none d-xl-flex flex-grow-1" sx={{ ml: 1 }}>
          {(isBooking ? config.services : config.nav).map((item, i) => {
            if (item.type === "link") return <NavLink key={i} isBooking={isBooking} href={item.href} target={item?.target}>{item.label}</NavLink>;
            if (item.type === "menu") {
              const open = Boolean(menuAnchors[i]);
              return (
                <Stack key={i} direction="row" alignItems="center">
                  <Button color="inherit" endIcon={<ExpandMoreIcon fontSize="small" />} sx={btnLinkSx} onClick={(e) => openMenu(i, e.currentTarget)}>
                    {item.label}
                  </Button>
                  <Menu anchorEl={menuAnchors[i]} open={open} onClose={() => closeMenu(i)} slotProps={{ paper: { sx: { mt: 1, borderRadius: 2 } } }}>
                    {item.items.map((mi, k) =>
                      mi.type === "divider" ? <Divider key={k} /> : (
                        <MenuItem key={k} onClick={() => closeMenu(i)} component={Link} href={mi.href || "#"} target={mi?.target ? "_blank" : undefined} rel={mi?.target ? "noopener noreferrer" : undefined}>
                          {mi.label}
                        </MenuItem>
                      )
                    )}
                  </Menu>
                </Stack>
              );
            }
            return null;
          })}
        </Stack>

        {/* Desktop right */}
        <Stack direction="row" alignItems="center" gap={1} className="d-none d-xl-flex ms-auto">
          {/* App badges */}
          {config.appBadges?.enabled && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 1 }}>

              {/* MAIN CAR ICON (filled cyan center) */}
              {showCard && (
                <Box sx={{ position: "relative", display: "inline-flex", mr: 1 }}>
                  <motion.div
                    animate={{ scale: isExpired ? [1, 1.1, 1] : [1, 1.06, 1] }}
                    transition={{
                      duration: 1.8,
                      repeat: Infinity,
                      repeatType: "mirror",
                      ease: "easeInOut",
                    }}
                    whileHover={{ scale: 1.08 }}
                    style={{ borderRadius: "50%" }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setShowStatusCard(!showStatusCard)}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: isExpired ? "rgba(40,8,8,0.95)" : "#03171f",
                        border: isExpired
                          ? "1px solid rgba(255,107,107,0.9)"
                          : "1px solid #7ffcfd",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        transition: "all 0.25s ease",
                        "&:hover": {
                          borderColor: isExpired ? "rgba(255,107,107,1)" : "#7ffcfd",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          background: isExpired
                            ? "linear-gradient(135deg, #3a0b0b, #6b1515)"
                            : "radial-gradient(circle at 30% 25%, #7ffcfd 0%, #02818a 45%, #02141a 100%)",
                          borderRadius: "50%",
                          width: 28,
                          height: 28,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: isExpired
                            ? "1px solid rgba(255,107,107,0.9)"
                            : "1px solid #7ffcfd",
                        }}
                      >
                        <DirectionsCarFilledIcon sx={{ fontSize: 18, color: "#ffffff" }} />
                      </Box>
                    </IconButton>
                  </motion.div>
                </Box>
              )}

              {showStatusCard && (
                <Box
                  sx={{
                    position: "fixed",
                    inset: 0,
                    zIndex: (theme) => theme.zIndex.modal + 10,
                    pointerEvents: "none", // container ignores clicks
                  }}
                >
                  <motion.div
                    drag                 // 🔹 enable drag
                    dragMomentum={false}
                    initial={{ opacity: 0, scale: 0.9, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      cursor: "grab",
                      pointerEvents: "auto", // card is still interactive
                    }}
                    whileDrag={{ cursor: "grabbing" }}
                  >
                    <DriverStatusCard
                      show={showStatusCard}
                      tripId={tripId}
                      onClose={() => setShowStatusCard(false)}
                    />
                  </motion.div>
                </Box>
              )}


              {
                chauffeurMessage &&
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <AnimatePresence>
                    {showInfo && (
                      <motion.div
                        initial={{ opacity: 0, x: 10 }}
                        animate={{
                          opacity: 1,
                          x: 0,
                          y: [0, -2, 0],
                        }}
                        exit={{ opacity: 0, x: 10 }}
                        transition={{
                          opacity: { duration: 0.25 },
                          x: { duration: 0.25 },
                          y: {
                            duration: 2,
                            repeat: Infinity,
                            repeatType: "reverse",
                            ease: "easeInOut",
                          },
                        }}
                        style={{
                          position: "absolute",
                          right: "110%",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background:
                            "linear-gradient(135deg, rgba(2,20,26,0.96), rgba(3,39,47,0.98))",
                          borderRadius: "12px",
                          padding: "10px 14px",
                          border: "1px solid rgba(127,252,253,0.5)",
                          minWidth: "300px",
                          fontSize: "13px",
                          color: "#e6feff",
                          zIndex: 2,
                          pointerEvents: "none",
                          textAlign: "center",
                        }}
                      >
                        {chauffeurMessage ||
                          "Your personal chauffeur subscription info will appear here."}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Pulsing wrapper, stronger pulse if expired */}
                  <motion.div
                    animate={{
                      scale: isExpired ? [1, 1.1, 1] : [1, 1.06, 1],
                    }}
                    transition={{
                      duration: 1.8,
                      repeat: Infinity,
                      repeatType: "mirror",
                      ease: "easeInOut",
                    }}
                    whileHover={{ scale: 1.08 }}
                    style={{ borderRadius: "50%" }}
                  >
                    <IconButton
                      size="small"
                      onMouseEnter={() => setShowInfo(true)}
                      onMouseLeave={() => setShowInfo(false)}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: "rgba(2,20,26,0.9)",
                        border: isExpired
                          ? "1px solid rgba(255,107,107,0.85)" // red border
                          : "1px solid rgba(127,252,253,0.6)", // cyan border
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        transition: "all 0.25s ease",
                        "&:hover": {
                          borderColor: isExpired
                            ? "rgba(255,107,107,1)"
                            : "rgba(127,252,253,0.9)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: isExpired ? "rgba(40,8,8,0.95)" : "#02141a",
                          borderRadius: "50%",
                          width: 28,
                          height: 28,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: isExpired
                            ? "1px solid rgba(255,107,107,0.9)"
                            : "1px solid rgba(127,252,253,0.4)",
                        }}
                      >
                        {isExpired ? (
                          <CarCrashIcon sx={{ fontSize: 18, color: "#ff6b6b" }} />
                        ) : (
                          <img
                            src="/img/personal-chauffeur/driver-white.png"
                            alt="Chauffeur"
                            style={{ width: 18, height: 18 }}
                          />
                        )}
                      </Box>
                    </IconButton>
                  </motion.div>
                </Box>
              }

              <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 600 }}>
                {config.appBadges.label}
              </Typography>
              {config.appBadges.stores?.map((s, idx) => (
                s.type === "android" && !shouldShowPlayStore() ? null :
                  <Tooltip key={idx} title={s.type === "apple" ? "Download on the App Store" : "Get it on Google Play"}>
                    <IconButton size="small" component={Link} href={s.href || "#"} sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#fff", border: "1px solid #e5e9ee", boxShadow: "0 1px 2px rgba(0,0,0,0.06)" }}>
                      <Box sx={{ position: "relative", width: 16, height: 16 }}>
                        <Image src={s.img} alt={s.type} fill style={{ objectFit: "contain" }} />
                      </Box>
                    </IconButton>
                  </Tooltip>
              ))}
            </Stack>
          )}

          {/* Search */}
          {config.search?.enabled && !session && (
            <Paper component="form" elevation={0} sx={{ height: 36, minWidth: 120, pl: 1.25, pr: 0.5, display: "flex", alignItems: "center", gap: 1, borderRadius: "20px", bgcolor: bgColor ? "#fff" : "#f5f7f8", border: "1px solid #e9edf2" }} onSubmit={(e) => e.preventDefault()}>
              <InputBase onMouseDown={goSearch}
                onKeyDown={goSearchKey} placeholder={config.search.placeholder || "Search..."} sx={{ flex: 1, fontSize: 14, lineHeight: 1, width: "20px", "& input::placeholder": { color: "#9aa1a7", opacity: 1 } }} />
              <IconButton size="small" aria-label="search" disableRipple sx={{ width: 28, height: 28, p: 0, borderRadius: "50%", bgcolor: "#fff", "&:hover": { bgcolor: "#fff" }, cursor: "pointer" }} onClick={goSearch}>
                <Image src="/img/header/search.svg" alt="search" width={14} height={14} />
              </IconButton>
            </Paper>
          )}

          {/* Avatar or CTA */}
          {!shouldShowPlayStore() ? null : isLoggedIn ? (
            <>
              <Tooltip title={displayEmail || ""}>
                <IconButton onClick={handleAccountOpen} size="small" sx={{ ml: 1 }}>
                  <Avatar sx={{ bgcolor: deepOrange[500], textTransform: "uppercase" }}>
                    {initials}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu anchorEl={accountEl} id="account-menu" open={accountOpen} onClose={handleAccountClose} onClick={handleAccountClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: "visible",
                    mt: 1.5,
                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                    "& .MuiAvatar-root": { width: 32, height: 32, ml: -0.5, mr: 1 },
                    "&:before": {
                      content: '""',
                      display: "block",
                      position: "absolute",
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: "background.paper",
                      transform: "translateY(-50%) rotate(45deg)",
                      zIndex: 0,
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem onClick={() => router.push("/rsldashboard/db-settings")}>
                  <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
                  Settings
                </MenuItem>
                <MenuItem onClick={() => router.push("/rsldashboard/bookinglist")}>
                  <ListItemIcon><CalendarMonth fontSize="small" /></ListItemIcon>
                  Bookings & Trip
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <CtaButton label={config.cta?.label || "Log in"} onClick={() => setOpenReg(true)} sx={{ py: 0.5 }} arrowWidth={10} arrowHeight={10} size="small" />
          )}
        </Stack>

        {/* Mobile right: avatar (if logged in) + menu */}
        <Stack direction="row" alignItems="center" gap={1} className="d-xl-none ms-auto">
          <Stack direction="row" alignItems="center" className="d-flex d-xl-none ms-auto">
            {/* MAIN CAR ICON (filled cyan center) */}
            {showCard && (
              <Box sx={{ position: "relative", display: "inline-flex", mr: 1 }}>
                <motion.div
                  animate={{ scale: isExpired ? [1, 1.1, 1] : [1, 1.06, 1] }}
                  transition={{
                    duration: 1.8,
                    repeat: Infinity,
                    repeatType: "mirror",
                    ease: "easeInOut",
                  }}
                  whileHover={{ scale: 1.08 }}
                  style={{ borderRadius: "50%" }}
                >
                  <IconButton
                    size="small"
                    onClick={() => setShowStatusCard(!showStatusCard)}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: isExpired ? "rgba(40,8,8,0.95)" : "#03171f",
                      border: isExpired
                        ? "1px solid rgba(255,107,107,0.9)"
                        : "1px solid #7ffcfd",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      transition: "all 0.25s ease",
                      "&:hover": {
                        borderColor: isExpired ? "rgba(255,107,107,1)" : "#7ffcfd",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        background: isExpired
                          ? "linear-gradient(135deg, #3a0b0b, #6b1515)"
                          : "radial-gradient(circle at 30% 25%, #7ffcfd 0%, #02818a 45%, #02141a 100%)",
                        borderRadius: "50%",
                        width: 28,
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: isExpired
                          ? "1px solid rgba(255,107,107,0.9)"
                          : "1px solid #7ffcfd",
                      }}
                    >
                      <DirectionsCarFilledIcon sx={{ fontSize: 18, color: "#ffffff" }} />
                    </Box>
                  </IconButton>
                </motion.div>
              </Box>
            )}

            {showStatusCard && (
              <Box
                sx={{
                  position: "fixed",
                  inset: 0,
                  zIndex: (theme) => theme.zIndex.modal + 10,
                  pointerEvents: "none", // container ignores clicks
                }}
              >
                <motion.div
                  drag                 // 🔹 enable drag
                  dragMomentum={false}
                  initial={{ opacity: 0, scale: 0.9, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: "10%",
                    // left: "50%",
                    // transform: "translate(-50%, -50%)",
                    cursor: "grab",
                    pointerEvents: "auto", // card is still interactive
                  }}
                  whileDrag={{ cursor: "grabbing" }}
                >
                  <DriverStatusCard
                    show={showStatusCard}
                    tripId={tripId}
                    onClose={() => setShowStatusCard(false)}
                  />
                </motion.div>
              </Box>
            )}

            {
              chauffeurMessage &&
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <AnimatePresence>
                  {showInfo && (
                    <motion.div
                      initial={{ opacity: 0, x: 10 }}
                      animate={{
                        opacity: 1,
                        x: 0,
                        y: [0, -2, 0],
                      }}
                      exit={{ opacity: 0, x: 10 }}
                      transition={{
                        opacity: { duration: 0.25 },
                        x: { duration: 0.25 },
                        y: {
                          duration: 2,
                          repeat: Infinity,
                          repeatType: "reverse",
                          ease: "easeInOut",
                        },
                      }}
                      style={{
                        position: "absolute",
                        right: "110%",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background:
                          "linear-gradient(135deg, rgba(2,20,26,0.96), rgba(3,39,47,0.98))",
                        borderRadius: "12px",
                        padding: "10px 14px",
                        border: "1px solid rgba(127,252,253,0.5)",
                        minWidth: "300px",
                        fontSize: "13px",
                        color: "#e6feff",
                        zIndex: 2,
                        pointerEvents: "none",
                        textAlign: "center",
                      }}
                    >
                      {chauffeurMessage ||
                        "Your personal chauffeur subscription info will appear here."}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Pulsing wrapper, stronger pulse if expired */}
                <motion.div
                  animate={{
                    scale: isExpired ? [1, 1.1, 1] : [1, 1.06, 1],
                  }}
                  transition={{
                    duration: 1.8,
                    repeat: Infinity,
                    repeatType: "mirror",
                    ease: "easeInOut",
                  }}
                  whileHover={{ scale: 1.08 }}
                  style={{ borderRadius: "50%" }}
                >
                  <IconButton
                    size="small"
                    onMouseEnter={() => setShowInfo(true)}
                    onMouseLeave={() => setShowInfo(false)}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(2,20,26,0.9)",
                      border: isExpired
                        ? "1px solid rgba(255,107,107,0.85)" // red border
                        : "1px solid rgba(127,252,253,0.6)", // cyan border
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      transition: "all 0.25s ease",
                      "&:hover": {
                        borderColor: isExpired
                          ? "rgba(255,107,107,1)"
                          : "rgba(127,252,253,0.9)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: isExpired ? "rgba(40,8,8,0.95)" : "#02141a",
                        borderRadius: "50%",
                        width: 28,
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: isExpired
                          ? "1px solid rgba(255,107,107,0.9)"
                          : "1px solid rgba(127,252,253,0.4)",
                      }}
                    >
                      {isExpired ? (
                        <CarCrashIcon sx={{ fontSize: 18, color: "#ff6b6b" }} />
                      ) : (
                        <img
                          src="/img/personal-chauffeur/driver-white.png"
                          alt="Chauffeur"
                          style={{ width: 18, height: 18 }}
                        />
                      )}
                    </Box>
                  </IconButton>
                </motion.div>
              </Box>
            }
          </Stack>
          {isLoggedIn && (
            <IconButton onClick={handleAccountOpen} size="small" sx={{ mr: 0.5 }}>
              <Avatar sx={{ bgcolor: deepOrange[500], width: 36, height: 36 }}>{initials}</Avatar>
            </IconButton>
          )}
          <IconButton aria-label="open menu" onClick={() => setDrawerOpen(true)} sx={{ width: 40, height: 40, borderRadius: "50%", bgcolor: "#fff", border: "1px solid #e5e9ee" }}>
            <MenuIcon />
          </IconButton>
        </Stack>
      </Paper>

      {/* Account menu (also used by mobile avatar) */}
      <Menu anchorEl={accountEl} id="account-menu-mobile" open={accountOpen} onClose={handleAccountClose} onClick={handleAccountClose}
        PaperProps={{ elevation: 0, sx: { overflow: "visible", mt: 1.5, filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))" } }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={() => router.push("/rsldashboard/db-settings")}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem onClick={() => router.push("/rsldashboard/bookinglist")}>
          <ListItemIcon><CalendarMonth fontSize="small" /></ListItemIcon>
          Bookings & Trip
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Mobile Drawer (design unchanged) */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: 300, borderRadius: "16px 0 0 16px" } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
          <Box sx={{ position: "relative", width: 84, height: 46 }}>
            <Image src={config.logo.src} alt="RSL" fill style={{ objectFit: "contain" }} />
          </Box>
          <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
        </Stack>
        <Divider />

        {config.search?.enabled && !session && (
          <Box sx={{ p: 2 }}>
            <Paper component="form" elevation={0} sx={{ height: 40, px: 1.25, display: "flex", alignItems: "center", gap: 1, borderRadius: "20px", bgcolor: "#f5f7f8", border: "1px solid #e6eaee" }}>
              <InputBase onMouseDown={goSearch}
                onKeyDown={goSearchKey} placeholder={config.search.placeholder || "Search..."} sx={{ flex: 1, fontSize: 14, "& input::placeholder": { color: "#9aa1a7", opacity: 1 }, cursor: "pointer" }} />
              <Image src="/img/header/search.svg" alt="search" width={14} height={14} onClick={goSearch} />
            </Paper>
          </Box>
        )}

        <List sx={{ px: 1 }}>
          {(isBooking ? config.services : config.nav).map((item, i) => {
            const isActive = isBooking ? routeMap[item.label]?.some(
              path => router.asPath === path || router.asPath.startsWith(path) || router.pathname === path
            ) : isActivePath(item?.href);
            if (item.type === "link") {
              return (
                <ListItemButton key={i} component={Link} href={item.href || "#"} target={item?.target ? "_blank" : undefined} rel={item?.target ? "noopener noreferrer" : undefined} onClick={() => setDrawerOpen(false)} sx={{ borderRadius: 2, mx: 1 }}>
                  <ListItemText primary={item.label} className={isActive ? 'active-current' : ''} primaryTypographyProps={{ fontWeight: 600 }} />
                </ListItemButton>
              );
            }
            if (item.type === "menu") {
              const open = !!expanded[i];
              return (
                <Box key={i} sx={{ borderRadius: 2, mx: 1 }}>
                  <ListItemButton onClick={() => toggleExpand(i)} sx={{ borderRadius: 2 }}>
                    <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 700 }} />
                    {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemButton>
                  <Collapse in={open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pb: 1 }}>
                      {item.items.map((mi, k) =>
                        mi.type === "divider" ? <Divider key={k} sx={{ my: 1 }} /> : (
                          <ListItemButton key={k} component={Link} href={mi.href || "#"} onClick={() => setDrawerOpen(false)} sx={{ pl: 3, borderRadius: 2, mx: 1 }}>
                            <ListItemText primary={mi.label} />
                          </ListItemButton>
                        )
                      )}
                    </List>
                  </Collapse>
                </Box>
              );
            }
            return null;
          })}
        </List>

        <Divider sx={{ my: 1 }} />

        <Stack spacing={1.5} sx={{ p: 2 }}>
          {config.appBadges?.enabled && (
            <Stack direction="row" alignItems="center" spacing={1}>
              {config.appBadges.stores?.map((s, idx) => (
                s.type === "android" && !shouldShowPlayStore() ? null :
                  <IconButton key={idx} component={Link} href={s.href || "#"} sx={{ width: 40, height: 40, borderRadius: "50%", bgcolor: "#fff", border: "1px solid #e5e9ee" }}>
                    <Box sx={{ position: "relative", width: 20, height: 20 }}>
                      <Image src={s.img} alt={s.type} fill style={{ objectFit: "contain" }} />
                    </Box>
                  </IconButton>
              ))}
            </Stack>
          )}

          {!shouldShowPlayStore() ? null : isLoggedIn ? (
            <Button
              fullWidth
              onClick={handleLogout}
              sx={{
                textTransform: "none",
                borderRadius: "9999px",
                py: 1.2,
                fontSize: 16,
                fontWeight: 600,
                color: "#0f7f8a",
                border: "1px solid #0f7f8a"
                // background: "#ff7a7a",
                // "&:hover": { background: "linear-gradient(180deg, #ff6a6a 0%, #d62839 100%)" },
              }}
            >
              Logout
            </Button>
          ) : (
            <Button
              fullWidth
              onClick={() => { setDrawerOpen(false); setOpenReg(true); }}
              sx={{
                textTransform: "none",
                borderRadius: "9999px",
                py: 1.2,
                fontSize: 16,
                fontWeight: 600,
                color: "#fff",
                background: "linear-gradient(180deg, #7fd0cb 0%, #0f7f8a 100%)",
                "&:hover": { background: "linear-gradient(180deg, #74c4bf 0%, #0e747f 100%)" },
              }}
            >
              {config.cta?.label || "Log in"}
            </Button>
          )}
        </Stack>
      </Drawer>

      {/* Sign in / Sign up dialog (unchanged visually) */}
      <Dialog
        open={openReg}
        aria-labelledby="auth-title"
        aria-describedby="auth-desc"
        className="sign-signup"
        sx={{
          "& .MuiDialog-paper": {
            margin: "32px",
            [theme.breakpoints.down("md")]: { margin: "8px" },
          },
        }}
      >
        <div className="px-md-3 px-0  py-15">
          <div className="d-flex flex-row justify-content-end" style={{ padding: "3px 20px" }}>
            <button onClick={handleCloseReg}>
              <img src="/img/icons/close.png" className="way_icon" alt="close" />
            </button>
          </div>

          {/* <ul className="nav nav-tabs border-0 d-flex py-0" role="tablist">
            <li className="nav-item flex-fill bg-light w-50" role="presentation">
              <button className="nav-link active fw-bold text-dark border rounded py-5 border-2 border-secondary-subtle w-100" id="user-tab" data-bs-toggle="pill" data-bs-target="#user" type="button" role="tab" aria-controls="user" aria-selected="true">
                User
              </button>
            </li>
            <li className="nav-item flex-fill bg-light w-50" role="presentation">
              <button className="nav-link fw-semibold text-secondary rounded py-5 border-0 w-100" type="button" onClick={() => window.open("https://admin.royalsmartlifestyle.com/")}>
                Corporate / B2B
              </button>
            </li>
          </ul> */}

          <div className="tab-content mt-2">
            <div className="tab-pane fade show active" id="user" role="tabpanel" aria-labelledby="pills-user-tab">
              <DialogTitle id="auth-title" style={{ padding: "3px 20px" }}>
                <h4 style={{ padding: 0 }}>Sign In / Register</h4>
              </DialogTitle>
              <DialogContent style={{ padding: "3px 20px" }}>
                <DialogContentText id="auth-desc" style={{ padding: "16px 0px" }}>
                  Manage your bookings with ease and enjoy member-only benefits
                </DialogContentText>
              </DialogContent>

              <DialogContent sx={{ padding: "20px" }}>
                {isSignUp ? (
                  <SignUp
                    setOpenReg={setOpenReg}
                    setIsSignUp={setIsSignUp}
                    viewStatus={viewStatus}
                    setViewStatus={setViewStatus}
                    setPrintMsg={setPrintMsg}
                    printMsg={printMsg}
                  />
                ) : (
                  <SignIn
                    setOtpLoading={setOtpLoading}
                    otpLoading={otpLoading}
                    printMsg={printMsg}
                    setPrintMsg={setPrintMsg}
                    refTocken={refTocken}
                    setOpenReg={setOpenReg}
                    setIsSignUp={setIsSignUp}
                    viewStatus={viewStatus}
                    setViewStatus={setViewStatus}
                  />
                )}
              </DialogContent>

              <div className="other_login_way">
                <div className="other_title other_title_thirdparty">
                  <i className="left-line"></i>
                  <div className="other_title_txt">More Sign In Methods</div>
                  <i className="right-line"></i>
                </div>
                <div className="ibu_thirdpartlogin_online_wrapper">
                  <div className="way_item" role="presentation">
                    <button className="way_icon" onClick={handleGoogleSignin}>
                      {!loading ? (
                        <img src="/img/icons/search.png" alt="Google Icon" />
                      ) : (
                        <CircularProgress sx={{ color: "red" }} size={40} thickness={5} />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="ibu_login_c_desc">
                <span>
                  By signing in or registering, you are deemed to have agreed to our <br />
                  <a href="https://royalsmartlifestyle.com/others-pages/termsAndPolicies" target="_blank" rel="noopener noreferrer" className="privacy_policy_link_txt" style={{ textDecoration: "underline" }}>
                    Terms and Conditions{" "}
                  </a>
                  and{" "}
                  <a href="https://royalsmartlifestyle.com/others-pages/privacyUrl" target="_blank" rel="noopener noreferrer" className="privacy_policy_link_txt" style={{ textDecoration: "underline" }}>
                    Privacy Statement
                  </a>
                  .
                </span>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </AppBar>
  );
}

const normalizePath = (p = "") =>
  p?.split("?")?.[0]?.split("#")?.[0]?.replace(/\/+$/, "") || "/";

/* helpers */
function NavLink({ href = "#", children, target, isBooking }) {
  const router = useRouter();
  // const isActive = isBooking ? routeMap[children]?.some(
  //   path => router.asPath === path || router.asPath.startsWith(path) || router.pathname === path
  // ) : useIsActive(href);
  const pathname = usePathname();
  const isActivePath = useCallback(
    (href) => isEqualPath(pathname, href),
    [pathname]
  );

  const isActive = isBooking
    ? routeMap[children]?.some(
      path =>
        router.asPath === path ||
        router.asPath.startsWith(path) ||
        router.pathname === path
    )
    : isActivePath(href);
  return (
    <Link href={href} target={target ? "_blank" : undefined} rel={target ? "noopener noreferrer" : undefined} className="text-decoration-none">
      <Typography variant="body2" className={isActive ? 'active-current' : ''} sx={{ fontWeight: 600, "&:hover": { color: "#6cc8bf" } }}>
        {children}
      </Typography>
    </Link>
  );
}

const btnLinkSx = {
  color: "text.primary",
  textTransform: "none",
  fontWeight: 600,
  px: 1,
  "& .MuiButton-endIcon": { ml: 0.25 },
  "&:hover": { backgroundColor: "transparent", color: "#6cc8bf" },
};
